import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './OwlDelivery.module.css';

/**
 * OwlDelivery — انیمیشن سینمایی «جغد نامه‌رسان» قبل از نمایش پیام محرمانه.
 *
 * فازها:
 *  fly    → جغد (SVG کدنویسی‌شده، بدون تصویر) با بال‌زدن از دوردست وارد آسمان شب می‌شود و پاکت را حمل می‌کند
 *  drop   → جغد پاکت را رها می‌کند و از کادر خارج می‌شود؛ پاکت چرخان به مرکز صفحه می‌افتد
 *  sealed → پاکت با آدرس گیرنده (جوهر سبز زمردی، مثل فیلم) و مهرِ موم؛ منتظر لمس مهر
 *  open   → درِ پاکت باز می‌شود، نامه بیرون می‌آید
 *  fade   → کل صحنه محو می‌شود و صفحه‌ی اصلی نمایان می‌گردد
 *
 * همه‌چیز CSS/SVG خالص است — هیچ فایل تصویری‌ای لود نمی‌شود.
 */

const PHASE = {
  FLY: 'fly',
  DROP: 'drop',
  SEALED: 'sealed',
  OPEN: 'open',
  FADE: 'fade',
};

// ── جغد SVG — بال‌ها گروه‌های جدا هستند تا با CSS بال بزنند ────────────────────
function OwlSvg() {
  return (
    <svg viewBox="0 0 240 200" className={styles.owlSvg} aria-hidden="true">
      <defs>
        <radialGradient id="owlBody" cx="50%" cy="35%" r="75%">
          <stop offset="0%" stopColor="#b98d5e" />
          <stop offset="55%" stopColor="#8a6138" />
          <stop offset="100%" stopColor="#5d3f21" />
        </radialGradient>
        <radialGradient id="owlBelly" cx="50%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#f3e6cd" />
          <stop offset="100%" stopColor="#cfb489" />
        </radialGradient>
        <linearGradient id="owlWing" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7c5530" />
          <stop offset="100%" stopColor="#4a3016" />
        </linearGradient>
        <radialGradient id="owlEye" cx="35%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#ffd873" />
          <stop offset="70%" stopColor="#e8a825" />
          <stop offset="100%" stopColor="#9a6a08" />
        </radialGradient>
      </defs>

      {/* بال راست (سمت بیننده: چپ) */}
      <g className={styles.wingLeft}>
        <path
          d="M96 88 C 60 60, 18 52, 4 76 C 24 74, 34 82, 30 92 C 48 86, 58 92, 55 102 C 70 96, 82 100, 84 110 Z"
          fill="url(#owlWing)"
        />
      </g>
      {/* بال چپ */}
      <g className={styles.wingRight}>
        <path
          d="M144 88 C 180 60, 222 52, 236 76 C 216 74, 206 82, 210 92 C 192 86, 182 92, 185 102 C 170 96, 158 100, 156 110 Z"
          fill="url(#owlWing)"
        />
      </g>

      {/* دم */}
      <path d="M108 150 L120 178 L132 150 Z" fill="#5d3f21" />

      {/* بدن */}
      <ellipse cx="120" cy="112" rx="34" ry="46" fill="url(#owlBody)" />
      <ellipse cx="120" cy="122" rx="22" ry="32" fill="url(#owlBelly)" />
      {/* خط‌های پر روی سینه */}
      <g stroke="#a8895c" strokeWidth="1.4" fill="none" opacity="0.7">
        <path d="M110 108 q5 5 10 0 q5 5 10 0" />
        <path d="M108 120 q6 5 12 0 q6 5 12 0" />
        <path d="M110 132 q5 5 10 0 q5 5 10 0" />
      </g>

      {/* سر */}
      <circle cx="120" cy="66" r="30" fill="url(#owlBody)" />
      {/* گوش‌پرها */}
      <path d="M96 48 L90 26 L108 40 Z" fill="#6b4a26" />
      <path d="M144 48 L150 26 L132 40 Z" fill="#6b4a26" />
      {/* صفحه‌ی صورت */}
      <ellipse cx="108" cy="66" rx="13" ry="15" fill="#e9d9ba" opacity="0.9" />
      <ellipse cx="132" cy="66" rx="13" ry="15" fill="#e9d9ba" opacity="0.9" />
      {/* چشم‌ها */}
      <circle cx="108" cy="66" r="9" fill="url(#owlEye)" />
      <circle cx="132" cy="66" r="9" fill="url(#owlEye)" />
      <circle cx="108" cy="66" r="4" fill="#1c1108" />
      <circle cx="132" cy="66" r="4" fill="#1c1108" />
      <circle cx="106" cy="63" r="1.6" fill="#fff" opacity="0.9" />
      <circle cx="130" cy="63" r="1.6" fill="#fff" opacity="0.9" />
      {/* منقار */}
      <path d="M116 72 L124 72 L120 82 Z" fill="#d99a2b" />

      {/* پاها */}
      <g className={styles.owlFeet}>
        <path d="M112 152 l-3 10 m3 -10 l0 11 m0 -11 l3 10" stroke="#d99a2b" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M128 152 l-3 10 m3 -10 l0 11 m0 -11 l3 10" stroke="#d99a2b" strokeWidth="2.4" strokeLinecap="round" />
      </g>
    </svg>
  );
}

// ── مهر موم SVG — برجسته با نشان H ─────────────────────────────────────────────
function WaxSealSvg({ broken }) {
  return (
    <svg viewBox="0 0 120 120" className={`${styles.sealSvg} ${broken ? styles.sealBroken : ''}`} aria-hidden="true">
      <defs>
        <radialGradient id="sealWax" cx="38%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#c22a2e" />
          <stop offset="55%" stopColor="#8f1417" />
          <stop offset="100%" stopColor="#560608" />
        </radialGradient>
        <radialGradient id="sealShine" cx="30%" cy="22%" r="45%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* موم بی‌قاعده با قطره‌ها */}
      <path
        d="M60 6 C82 4 104 18 110 40 C116 60 112 78 100 92 C90 104 74 114 58 112 C40 110 22 102 14 86 C5 68 6 46 18 30 C30 14 44 8 60 6 Z"
        fill="url(#sealWax)"
      />
      <circle cx="14" cy="74" r="7" fill="#7c1013" />
      <circle cx="106" cy="52" r="5" fill="#7c1013" />
      {/* حلقه‌ی برجسته */}
      <circle cx="60" cy="60" r="38" fill="none" stroke="#5f0a0c" strokeWidth="3" opacity="0.9" />
      <circle cx="60" cy="60" r="34" fill="none" stroke="#d4696c" strokeWidth="1.4" opacity="0.55" />
      {/* نشان H با تاج */}
      <text
        x="60"
        y="74"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontWeight="700"
        fontSize="42"
        fill="#4a0507"
        stroke="#e08d8f"
        strokeWidth="0.6"
      >
        H
      </text>
      <path d="M48 30 l6 6 6 -8 6 8 6 -6 -2 10 h-20 Z" fill="#4a0507" stroke="#d4696c" strokeWidth="0.5" />
      {/* برق موم */}
      <ellipse cx="44" cy="36" rx="22" ry="14" fill="url(#sealShine)" />
    </svg>
  );
}

export default function OwlDelivery({ firstName = '', lastName = '', addressLines = [], onDone }) {
  const [phase, setPhase] = useState(PHASE.FLY);
  const timers = useRef([]);

  const later = useCallback((fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  const finish = useCallback(() => {
    timers.current.forEach(clearTimeout);
    if (onDone) onDone();
  }, [onDone]);

  useEffect(() => {
    // fly (جغد وارد می‌شود) → drop (پاکت رها می‌شود) → sealed (منتظر لمس مهر)
    later(() => setPhase(PHASE.DROP), 3400);
    later(() => setPhase(PHASE.SEALED), 4800);
    return () => timers.current.forEach(clearTimeout);
  }, [later]);

  // با اسکرین‌ریدرها: کل صحنه تزئینی است؛ دکمه‌ی رد شدن همیشه در دسترس است.
  function handleOpen() {
    if (phase !== PHASE.SEALED) return;
    setPhase(PHASE.OPEN);
    later(() => setPhase(PHASE.FADE), 2300);
    later(finish, 3100);
  }

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'جادوگر جوان';
  const lines = (addressLines || []).filter(Boolean);

  return (
    <div
      className={`${styles.scene} ${phase === PHASE.FADE ? styles.sceneFade : ''}`}
      role="presentation"
    >
      {/* ── آسمان شب ── */}
      <div className={styles.sky}>
        <div className={styles.moon}>
          <span className={styles.crater1} />
          <span className={styles.crater2} />
          <span className={styles.crater3} />
        </div>
        {Array.from({ length: 46 }).map((_, i) => (
          <span
            key={i}
            className={styles.skyStar}
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              animationDelay: `${(i % 9) * 0.45}s`,
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
            }}
          />
        ))}
        <div className={`${styles.cloud} ${styles.cloud1}`} />
        <div className={`${styles.cloud} ${styles.cloud2}`} />
        <div className={`${styles.cloud} ${styles.cloud3}`} />
        <div className={styles.castle} />
      </div>

      {/* ── جغد ── */}
      {(phase === PHASE.FLY || phase === PHASE.DROP) && (
        <div className={`${styles.owl} ${phase === PHASE.DROP ? styles.owlExit : styles.owlEnter}`}>
          <OwlSvg />
          {/* پاکت آویزان از چنگال جغد — تا لحظه‌ی رها شدن */}
          {phase === PHASE.FLY && <div className={styles.carriedEnvelope} />}
        </div>
      )}

      {/* ── پاکت ── */}
      {(phase === PHASE.DROP || phase === PHASE.SEALED || phase === PHASE.OPEN || phase === PHASE.FADE) && (
        <div
          className={[
            styles.envelopeWrap,
            phase === PHASE.DROP ? styles.envelopeFall : '',
            phase === PHASE.OPEN || phase === PHASE.FADE ? styles.envelopeOpenState : '',
          ].join(' ')}
        >
          <div className={styles.envelope}>
            {/* بدنه‌ی پاکت */}
            <div className={styles.envelopeBack} />
            <div className={styles.envelopeFoldLeft} />
            <div className={styles.envelopeFoldRight} />

            {/* نامه‌ی داخل پاکت — هنوز خبری از پذیرش نیست؛ فقط یک پیام محرمانه.
                سرنوشت (دعوت شدن یا نشدن) بعد از ثبت‌نام و لیست انتظار مشخص می‌شود. */}
            <div className={styles.letterSheet}>
              <div className={styles.letterCrest}>⚡</div>
              <div className={styles.letterHead}>اداره‌ی پست جغدی هاگوارتز</div>
              <div className={styles.letterLine}>{fullName} عزیز،</div>
              <div className={styles.letterLineSmall}>پیامی محرمانه از دنیای جادو برای تو رسیده است؛ آن را بخوان تا بدانی چه در انتظار توست...</div>
            </div>

            {/* درِ پاکت (فلپ) */}
            <div className={styles.envelopeFlap}>
              <div className={styles.envelopeFlapInner} />
            </div>

            {/* روی پاکت — آدرس با جوهر سبز زمردی مثل فیلم */}
            <div className={styles.envelopeFace}>
              <div className={styles.addressBlock}>
                <div className={styles.addressTo}>به دستِ شخصِ</div>
                <div className={styles.addressName}>{fullName}</div>
                {lines.length > 0 ? (
                  lines.map((l, i) => (
                    <div key={i} className={styles.addressLine}>{l}</div>
                  ))
                ) : (
                  <div className={styles.addressLine}>بزرگ‌ترین اتاقِ خانه</div>
                )}
              </div>
              <div className={styles.postmark}>
                <span>پست جغدی هاگوارتز</span>
                <span className={styles.postmarkStamp}>🦉</span>
              </div>
            </div>

            {/* مهر موم */}
            <button
              type="button"
              className={styles.sealBtn}
              onClick={handleOpen}
              aria-label="باز کردن نامه"
              disabled={phase !== PHASE.SEALED}
            >
              <WaxSealSvg broken={phase === PHASE.OPEN || phase === PHASE.FADE} />
            </button>
          </div>

          {phase === PHASE.SEALED && (
            <div className={styles.openHint}>برای گشودن نامه، مهرِ موم را لمس کن ✨</div>
          )}
        </div>
      )}

      {/* ── رد شدن ── */}
      <button type="button" className={styles.skipBtn} onClick={finish}>
        رد شدن ↵
      </button>
    </div>
  );
}
